import React, { useEffect, useState } from "react";
import { Alert } from "@mui/material";
import MarkdownIt from "markdown-it";
import markdownItSub from "markdown-it-sub";
import markdownItSup from "markdown-it-sup";
import markdownItFootnote from "markdown-it-footnote";
import markdownItDeflist from "markdown-it-deflist";
import markdownItAbbr from "markdown-it-abbr";
import markdownItIns from "markdown-it-ins";
import markdownItMark from "markdown-it-mark";
import markdownItContainer from "markdown-it-container";
import markdownItAnchor from "markdown-it-anchor";
import {imgSize} from '@mdit/plugin-img-size';
import hljs from "highlight.js";
import "highlight.js/styles/github.css";

const configureMarkdownIt = () => {
  const md = new MarkdownIt({
    html: true,
    xhtmlOut: false,
    breaks: false,
    langPrefix: "language-",
    linkify: true,
    typographer: true,
    highlight: function (str, lang) {
      if (lang && hljs.getLanguage(lang)) {
        try {
          return (
            '<pre class="hljs"><code>' +
            hljs.highlight(str, { language: lang, ignoreIllegals: true })
              .value +
            "</code></pre>"
          );
        } catch (__) {}
      }
      return (
        '<pre class="hljs"><code>' + md.utils.escapeHtml(str) + "</code></pre>"
      );
    },
  });

  md.use(markdownItSub)
    .use(markdownItSup)
    .use(markdownItFootnote)
    .use(markdownItDeflist)
    .use(markdownItAbbr)
    .use(markdownItIns)
    .use(markdownItMark)
    .use(markdownItAnchor)
    .use(imgSize)
    .use(markdownItContainer, "warning")
    .use(markdownItContainer, "info")
    .use(markdownItContainer, "success");

  const containers = {
    warning: { className: "markdown-warning", icon: "⚠️" },
    info: { className: "markdown-info", icon: "ℹ️" },
    success: { className: "markdown-success", icon: "✅" },
  };

  Object.keys(containers).forEach((name) => {
    const { className, icon } = containers[name];
    md.renderer.rules[`container_${name}_open`] = function () {
      return `<div class="${className}"><p class="container-title">${icon} ${name.toUpperCase()}</p>`;
    };
    md.renderer.rules[`container_${name}_close`] = function () {
      return "</div>";
    };
  });

  md.renderer.rules.emoji = function (token, idx) {
    return `<span class="emoji">${token[idx].content}</span>`;
  };

  return md;
};

const MarkdownRenderer = ({ content, errorMessage }) => {
  const [htmlContent, setHtmlContent] = useState("");
  const [error, setError] = useState(null);

  useEffect(() => {
    if (errorMessage) {
      setError(errorMessage);
      setHtmlContent("");
      return;
    }

    if (!content) {
      setError("Il contenuto del file markdown è vuoto o non disponibile.");
      setHtmlContent("");
      return;
    }

    try {
      const md = configureMarkdownIt();
      const html = md.render(content);
      setHtmlContent(html);
      setError(null);
    } catch (err) {
      console.error("Errore nel rendering del markdown:", err);
      setError(
        "Si è verificato un errore nel rendering del markdown. Controlla la console per dettagli.",
      );
      setHtmlContent("");
    }
  }, [content, errorMessage]);

  useEffect(() => {
    const container = document.querySelector(".markdown-container");
    if (!container) return;

    const onClick = (e) => {
      const a = e.target.closest('a[href^="#"]');
      if (!a) return;

      const hash = a.getAttribute("href");
      if (!hash || hash === "#") return;

      const id = hash.slice(1);

      let target = document.getElementById(id);

      if (!target && window.CSS && CSS.escape) {
        const safe = `#${CSS.escape(id)}`;
        target = container.querySelector(safe) || document.querySelector(safe);
      } else if (!target) {
        target =
          container.querySelector(`[id="${id}"]`) ||
          document.querySelector(`[id="${id}"]`);
      }

      if (!target) return;

      e.preventDefault();
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    container.addEventListener("click", onClick);
    return () => container.removeEventListener("click", onClick);
  }, []);

  if (error) {
    return (
      <div className="markdown-error">
        <Alert severity="warning">
          {error}
        </Alert>
      </div>
    );
  }

  return (
    <div className="markdown-container">
      <div dangerouslySetInnerHTML={{ __html: htmlContent }} />
    </div>
  );
};

export default MarkdownRenderer;